import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, ShieldCheck, Truck, Heart, Plus, Minus, ZoomIn } from "lucide-react";
import api, { formatPrice } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import ProductCard from "../components/ProductCard";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [selectedImg, setSelectedImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [related, setRelated] = useState([]);
  const [zoom, setZoom] = useState({ active: false, x: 50, y: 50 });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const imgRef = useRef(null);
  const { addItem } = useCart();

  useEffect(() => {
    setProduct(null);
    setSelectedImg(0);
    api.get(`/products/slug/${slug}`).then(r => {
      setProduct(r.data);
      api.get(`/products?category=${r.data.category}&limit=4`).then(rr => {
        setRelated(rr.data.filter(p => p.id !== r.data.id).slice(0, 4));
      });
    });
  }, [slug]);

  const onMove = (e) => {
    if (!imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoom({ active: true, x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  };
  const onLeave = () => setZoom(z => ({ ...z, active: false }));

  if (!product) {
    return <div className="pt-32 min-h-screen flex items-center justify-center"><div className="overline animate-pulse">Cargando...</div></div>;
  }

  const mainImage = product.images?.[selectedImg];

  return (
    <div data-testid="product-detail-page" className="pt-28 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* breadcrumb */}
        <div className="text-xs uppercase tracking-[0.2em] text-ivory/40 mb-10">
          <Link to="/" className="hover:text-gold">Inicio</Link> · <Link to={`/shop/${product.category}`} className="hover:text-gold">{product.category}</Link> · <span className="text-gold">{product.name}</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
          {/* Gallery with zoom */}
          <div>
            <div
              ref={imgRef}
              className="relative aspect-[4/5] overflow-hidden bg-graphite mb-4 cursor-zoom-in group"
              onMouseMove={onMove}
              onMouseLeave={onLeave}
              onClick={() => setLightboxOpen(true)}
              data-testid="zoom-area"
            >
              <motion.img
                key={selectedImg}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                src={mainImage}
                alt={product.name}
                className="w-full h-full object-cover transition-transform duration-200"
                style={zoom.active ? {
                  transformOrigin: `${zoom.x}% ${zoom.y}%`,
                  transform: "scale(2.5)",
                } : { transform: "scale(1)" }}
              />
              <div className={`absolute top-4 right-4 bg-carbon/70 backdrop-blur-sm border border-gold/40 p-2 transition-opacity ${zoom.active ? "opacity-0" : "opacity-100"}`}>
                <ZoomIn size={16} className="text-gold" />
              </div>
              <div className={`absolute bottom-4 right-4 text-[10px] uppercase tracking-[0.2em] text-ivory/70 transition-opacity ${zoom.active ? "opacity-100" : "opacity-0"}`}>
                250%
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {product.images?.map((img, i) => (
                <button
                  key={i}
                  data-testid={`thumb-${i}`}
                  onClick={() => setSelectedImg(i)}
                  className={`aspect-square overflow-hidden border ${i === selectedImg ? "border-gold" : "border-smoke"} hover:border-gold transition-colors`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
            <p className="text-xs text-ivory/40 uppercase tracking-wider mt-3 flex items-center gap-2">
              <ZoomIn size={11} /> Pasa el ratón sobre la imagen para hacer zoom · Click para vista completa
            </p>
          </div>

          {/* Info */}
          <div>
            {product.brand && <p className="overline mb-3">{product.brand}</p>}
            <h1 data-testid="product-name" className="font-heading text-4xl md:text-5xl text-ivory font-light tracking-tighter leading-tight">
              {product.name}
            </h1>
            <div className="flex items-center gap-3 mt-4">
              <div className="flex gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={14} fill={i < Math.round(product.rating) ? "#D4AF37" : "transparent"} stroke="#D4AF37" />
                ))}
              </div>
              <span className="text-sm text-ivory/60">{product.rating} · {product.reviews_count} reseñas</span>
            </div>

            <div className="flex items-baseline gap-4 mt-8">
              <span data-testid="product-price" className="price-detail">{formatPrice(product.price)}</span>
              {product.compare_at_price && (
                <span className="text-lg text-ivory/40 line-through" style={{ fontFamily: "Arial, sans-serif" }}>{formatPrice(product.compare_at_price)}</span>
              )}
            </div>
            <p className="text-xs text-ivory/40 uppercase tracking-wider mt-2">IVA incluido · Envío premium</p>

            {product.short_description && (
              <p className="mt-6 text-ivory/80 text-lg font-light leading-relaxed italic">"{product.short_description}"</p>
            )}

            <p className="mt-6 text-ivory/70 leading-relaxed font-light">{product.description}</p>

            {product.materials && (
              <div className="mt-8 pt-6 border-t border-smoke">
                <p className="overline mb-2">Materiales</p>
                <p className="text-sm text-ivory/80">{product.materials}</p>
              </div>
            )}

            {/* qty + cart */}
            <div className="mt-10 flex items-center gap-4">
              <div className="flex items-center border border-smoke">
                <button data-testid="qty-decrease" onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-3 text-ivory hover:text-gold"><Minus size={14} /></button>
                <span className="px-5 text-ivory" data-testid="qty-value">{qty}</span>
                <button data-testid="qty-increase" onClick={() => setQty(qty + 1)} className="px-4 py-3 text-ivory hover:text-gold"><Plus size={14} /></button>
              </div>
              <button
                data-testid="add-to-cart"
                onClick={() => addItem(product, qty)}
                className="btn-gold flex-1"
              >
                Añadir al carrito
              </button>
              <button data-testid="wishlist" className="border border-smoke p-4 text-ivory hover:text-gold hover:border-gold transition-colors">
                <Heart size={18} />
              </button>
            </div>

            {/* benefits */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 p-4 border border-smoke">
                <Truck size={20} className="text-gold mt-1" />
                <div>
                  <p className="text-sm text-ivory">Envío gratis desde 150€</p>
                  <p className="text-xs text-ivory/50 mt-1">Entrega en 24-48h</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 border border-smoke">
                <ShieldCheck size={20} className="text-gold mt-1" />
                <div>
                  <p className="text-sm text-ivory">Garantía Maison</p>
                  <p className="text-xs text-ivory/50 mt-1">Devolución 30 días</p>
                </div>
              </div>
            </div>

            <div className="mt-8 text-xs text-ivory/40 uppercase tracking-wider">
              SKU: {product.sku} {product.ean && `· EAN: ${product.ean}`}
            </div>
          </div>
        </div>

        {/* related */}
        {related.length > 0 && (
          <section className="mt-32">
            <div className="text-center mb-12">
              <p className="overline mb-3">Le sugerimos</p>
              <h2 className="font-heading text-3xl md:text-4xl text-ivory">También podría interesarle</h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </section>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[200] bg-carbon/95 flex items-center justify-center p-6"
          onClick={() => setLightboxOpen(false)}
          data-testid="lightbox"
        >
          <button onClick={() => setLightboxOpen(false)} className="absolute top-6 right-6 text-ivory hover:text-gold text-3xl">×</button>
          <img src={mainImage} alt={product.name} className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </div>
  );
}
